import React from "react";
import { Menu } from "@headlessui/react";
import { Link } from "react-router-dom";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { cn } from "../../DashboardPage/lib/utils";

/**
 * The canonical row-actions ("three dot") menu.
 *
 * Every table in the app had rolled its own — six different panel treatments and
 * two different trigger styles — so this owns the trigger, the panel, and the
 * transition.
 *
 * The panel is `anchor`ed, which makes Headless UI portal it to the document
 * body and position it against the trigger. That matters for more than tidiness:
 *
 *  - `DataTable`'s scroll container is `overflow-x-auto`, and per the CSS
 *    overflow spec an `overflow-y: visible` alongside a non-visible `overflow-x`
 *    computes to `auto`. So the container scrolls vertically too, and an
 *    absolutely-positioned panel opened on one of the last rows overflowed it
 *    and made an internal scrollbar appear.
 *  - Scrolling that container then closed the menu, which collapsed the
 *    scrollbar, which moved the scroll position, which fired another scroll —
 *    re-entering Headless UI's transition layout effects and blowing the React
 *    update-depth limit.
 *
 * Anchored, the panel is outside the clipping container entirely: no scrollbar
 * appears, and it tracks its trigger while the page scrolls instead of being
 * dismissed. `modal={false}` stays, because Headless UI v2 defaults `MenuItems`
 * to modal, which scroll-locks the body and hides the scrollbar (a visible
 * page shift).
 */

export const rowMenuTriggerClass =
  "border border-transparent p-2 transition-colors rounded-md text-neutral-600 hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus-visible:border-primary-700";

/**
 * Panel chrome. No `absolute`/`mt-*`: an anchored panel is positioned by the
 * library, and `gap` supplies the offset.
 */
export const rowMenuPanelClass =
  "z-50 w-48 py-1 bg-white border rounded-lg shadow-lg border-neutral-200 focus:outline-none";

/**
 * Same 100ms ease-out enter / 75ms ease-in leave the wrapped `<Transition>`
 * used, expressed with Headless UI v2's built-in `transition` prop.
 */
export const rowMenuTransitionClass =
  "origin-top-right transition ease-out duration-100 data-[closed]:opacity-0 data-[closed]:scale-95 data-[leave]:ease-in data-[leave]:duration-75";

export const rowMenuIconClass = "flex-shrink-0 w-4 h-4 mr-2.5";

export type RowMenuTone = "default" | "danger" | "accent";

export const rowMenuItemClass = (active: boolean, tone: RowMenuTone = "default") =>
  cn(
    "group flex w-full items-center px-4 py-2 text-sm transition-colors text-left",
    active && "bg-primary-50",
    tone === "danger"
      ? "text-danger-600"
      : tone === "accent"
        ? "text-primary-700"
        : "text-neutral-700"
  );

interface RowActionsMenuProps {
  children: React.ReactNode;
  /** Accessible name for the trigger. */
  label?: string;
  /** Extra classes for the positioning wrapper. */
  className?: string;
  /** Extra classes for the panel (e.g. a different width). */
  panelClassName?: string;
}

export default function RowActionsMenu({
  children,
  label = "Row actions",
  className,
  panelClassName,
}: RowActionsMenuProps) {
  return (
    <Menu as="div" className={cn("relative inline-block text-left", className)}>
      <Menu.Button aria-label={label} className={rowMenuTriggerClass}>
        <EllipsisVerticalIcon className="w-5 h-5" />
      </Menu.Button>
      <Menu.Items
        modal={false}
        transition
        anchor={{ to: "bottom end", gap: 4 }}
        className={cn(rowMenuPanelClass, rowMenuTransitionClass, panelClassName)}
      >
        {children}
      </Menu.Items>
    </Menu>
  );
}

/** Leading icon, tinted neutral unless the item itself carries a tone. */
function RowMenuIcon({ icon, tone }: { icon: React.ReactNode; tone: RowMenuTone }) {
  return (
    <span className={cn(rowMenuIconClass, tone === "default" && "text-neutral-500")}>
      {icon}
    </span>
  );
}

interface RowMenuItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  /** Rendered at the leading edge; sized and spaced by the menu. */
  icon?: React.ReactNode;
  tone?: RowMenuTone;
  disabled?: boolean;
}

interface RowMenuLinkProps {
  children: React.ReactNode;
  to: string;
  icon?: React.ReactNode;
  tone?: RowMenuTone;
}

/**
 * A navigating menu item. Prefer this over `RowMenuItem` + `navigate()` for
 * anything that goes to a route: it renders a real anchor, so middle-click and
 * ⌘/Ctrl-click still open in a new tab.
 */
export function RowMenuLink({ children, to, icon, tone = "default" }: RowMenuLinkProps) {
  return (
    <Menu.Item>
      {({ active }) => (
        <Link to={to} className={rowMenuItemClass(active, tone)}>
          {icon && <RowMenuIcon icon={icon} tone={tone} />}
          {children}
        </Link>
      )}
    </Menu.Item>
  );
}

export function RowMenuItem({
  children,
  onClick,
  icon,
  tone = "default",
  disabled = false,
}: RowMenuItemProps) {
  return (
    <Menu.Item disabled={disabled}>
      {({ active }) => (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={cn(rowMenuItemClass(active, tone), disabled && "opacity-40 cursor-not-allowed")}
        >
          {icon && <RowMenuIcon icon={icon} tone={tone} />}
          {children}
        </button>
      )}
    </Menu.Item>
  );
}
